// Per-child release picker (Form 8332): one checkbox per child with age and credit, writing proposed.releaseChildren.
// "Clear selection" removes the list so the Proposed panel goes back to the count-based `releases`.
import type { Case } from '@divorceos/schema';
import { useCaseView } from './caseView';
import { other } from './model';
import { releaseChildren } from './releases';
import { useDis } from './store';

export function ReleasePicker({ compact = false }: { compact?: boolean }) {
  const { c, labels: L, result } = useCaseView();
  const update = useDis((s) => s.update);
  if (c.children.length === 0) return <p className="muted" style={{ margin: 0 }}>No children entered, so there is nothing to release.</p>;
  const hi = result?.guideline.higherEarner ?? null;
  const releaser = hi === null ? null : other(hi);
  const kids = releaseChildren(c, releaser);
  const sel = c.proposed.releaseChildren;
  const picked = new Set(sel ?? []);
  const toggle = (i: number, on: boolean) => update((x: Case) => {
    const cur = new Set(x.proposed.releaseChildren ?? []);
    if (on) cur.add(i); else cur.delete(i);
    x.proposed.releaseChildren = [...cur].sort((a, b) => a - b);
  });
  const releasable = kids.filter((k) => k.releasable);
  return (
    <div className="relpick">
      {!compact && <p className="muted" style={{ margin: '0 0 6px', fontSize: 11.5 }}>
        {releaser === null ? 'Releases go from the parent with the lower net income to the other parent.' : <>Released by <b>{L[releaser]}</b> (lower net income) to <b>{L[other(releaser)]}</b>. Tick the children to release.</>}
      </p>}
      <table className="tbl">
        <thead><tr><th>Release</th><th>Child</th><th className="n">Age</th><th>Credit</th><th>Lives with</th></tr></thead>
        <tbody>
          {kids.map((k) => (
            <tr key={k.index} className={k.releasable ? '' : 'muted'}>
              <td><input type="checkbox" aria-label={`Release ${k.name}`} disabled={!k.releasable || !c.settings.releaseExemptions8332} checked={k.releasable && picked.has(k.index)} onChange={(e) => toggle(k.index, e.target.checked)} /></td>
              <td>{k.name}{k.releasable ? '' : <span className="muted"> · {k.why}</span>}</td>
              <td className="n">{k.age ?? '—'}</td>
              <td title={k.credit === 'CTC' ? 'Child tax credit (under 17 at year end)' : 'Credit for other dependents (17 or older)'}>{k.credit}</td>
              <td>{L[k.custodial]}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className="muted" style={{ fontSize: 11.5 }}>
          {!c.settings.releaseExemptions8332 ? 'Releases are not allowed (Dependency releases setting).'
            : sel === undefined ? `No selection: count-based release (${c.proposed.releases === null ? 'every available dependent' : `up to ${c.proposed.releases}`}; now ${result ? result.proposed.releases : '—'}).`
              : `Selected: ${sel.length ? sel.map((i) => kids[i]?.name ?? `child ${i + 1}`).join(', ') : 'none'}${result ? ` · applied ${result.proposed.releases}` : ''}.`}
        </span>
        <span style={{ flex: 1 }} />
        {releasable.length > 0 && c.settings.releaseExemptions8332 && <button type="button" className="btn sm" onClick={() => update((x) => { x.proposed.releaseChildren = releasable.map((k) => k.index); })}>Select all</button>}
        <button type="button" className="btn sm" disabled={sel === undefined} onClick={() => update((x) => { delete x.proposed.releaseChildren; })}>Clear selection</button>
      </div>
    </div>
  );
}
